import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePoll } from "@/components/common";
import { RefreshCw } from "lucide-react";

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [recon, setRecon] = useState(null);
  const load = () => api.get(`/admin/audit${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setLogs(r.data)).catch(() => {});
  usePoll(load, 8000, [q]);

  const runRecon = async () => {
    try { const { data } = await api.get("/admin/reconciliation"); setRecon(data); toast.success("Reconciliation complete"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Audit & Reconciliation</h1>
        <button onClick={runRecon} data-testid="run-reconciliation-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
          <RefreshCw className="w-4 h-4" /> Run reconciliation
        </button>
      </div>

      {recon && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="recon-results">
          <ReconCard label="Duplicate TXIDs" items={recon.duplicate_txids} />
          <ReconCard label="Duplicate references" items={recon.duplicate_references} />
          <ReconCard label="Order/payout mismatches" items={recon.order_payout_mismatches.map((m) => m.order_id)} />
          <div className="bg-white border border-slate-200 rounded-2xl p-4"><div className="text-xs uppercase tracking-wider text-slate-500">Excess deduction entries</div><div className="mt-1.5 text-2xl font-bold font-mono">{recon.excess_deduction_entries}</div></div>
        </div>
      )}

      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by action (e.g. payout, excess, login)" data-testid="audit-search"
        className="w-full max-w-md px-4 py-2.5 rounded-xl border border-slate-300 outline-none" />

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin max-h-[600px]">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-200">
              <th className="py-3 px-4">Action</th><th className="py-3 px-4">Actor</th><th className="py-3 px-4">Related</th><th className="py-3 px-4">Reason</th><th className="py-3 px-4">When</th>
            </tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-slate-100">
                  <td className="py-2.5 px-4 font-medium text-slate-800">{l.action}</td>
                  <td className="py-2.5 px-4 text-slate-500">{l.actor_role}</td>
                  <td className="py-2.5 px-4 font-mono text-xs text-slate-400">{Object.values(l.related || {}).join(" ")}</td>
                  <td className="py-2.5 px-4 text-slate-500 max-w-xs truncate">{l.reason}</td>
                  <td className="py-2.5 px-4 text-slate-400 whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ReconCard({ label, items }) {
  const ok = !items || items.length === 0;
  return (
    <div className={`rounded-2xl border p-4 ${ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1.5 text-2xl font-bold font-mono ${ok ? "text-emerald-700" : "text-rose-700"}`}>{items?.length || 0}</div>
      {!ok && <div className="text-xs text-rose-600 mt-1 break-all">{items.join(", ")}</div>}
    </div>
  );
}
